// src/components/lesson/diagrams/ComparisonTable.tsx
//
// Simple comparison table — useful for "transition types and what they signal,"
// "punctuation rules and when to use them," etc.
//
// Params:
//   {
//     "title": "Transition types",
//     "headers": ["Relationship", "Words", "Example"],
//     "rows": [
//       ["Contrast", "however, in contrast", "yet, despite this"],
//       ["Cause/effect", "therefore, as a result", "thus, consequently"]
//     ]
//   }

type ComparisonTableParams = {
  title?: string
  headers?: string[]
  rows?: Array<string[]>
  label?: string
}

export default function ComparisonTable({ params }: { params: Record<string, unknown> }) {
  const p = params as ComparisonTableParams
  const title = typeof p.title === 'string' ? p.title : null
  const headers = Array.isArray(p.headers)
    ? p.headers.filter((h): h is string => typeof h === 'string')
    : []
  const rows = Array.isArray(p.rows)
    ? p.rows
        .filter((r): r is string[] => Array.isArray(r))
        .map((r) => r.map((c) => (typeof c === 'string' ? c : String(c))))
    : []

  if (rows.length === 0 || headers.length === 0) return null

  return (
    <figure
      // #44: no role="img" — this renders a real <table>, so screen readers
      // should read the header/row data natively rather than a flattened
      // image label. The visible title acts as the figure's caption.
      aria-label={p.label ?? title ?? 'Comparison table'}
      className="my-3 overflow-x-auto rounded-md border border-border bg-card/60"
    >
      {title && (
        <div className="border-b border-border px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </div>
      )}
      <table className="min-w-full divide-y divide-slate-800 text-sm">
        <thead className="bg-card/80">
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/80">
          {rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-card/60">
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={
                    'px-3 py-2 align-top ' +
                    (ci === 0 ? 'font-medium text-foreground' : 'text-muted-foreground')
                  }
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  )
}
